import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, Tag, Check, ArrowRight, HelpCircle } from "lucide-react";
import * as Icons from "lucide-react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SERVICES } from "@/data/site";
import { useConsultation } from "@/context/ConsultationContext";

const get = (slug) => SERVICES.find((s) => s.slug === slug);

export default function ServicePage() {
  const { slug } = useParams();
  const s = get(slug);
  const { openConsultation } = useConsultation();
  if (!s) return <Navigate to="/404" replace />;
  const Icon = Icons[s.icon] || Icons.Sparkles;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.title,
    description: s.summary,
    provider: { "@type": "Organization", name: "Effizent Europe" },
    areaServed: "Portugal",
    mainEntity: {
      "@type": "FAQPage",
      mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  };

  return (
    <Layout ctaProps={{ interest: s.title, title: `Ready for ${s.title}?`, text: s.tagline }}>
      <Seo title={`${s.title} in Portugal | Effizent Europe`} description={s.summary} jsonLd={jsonLd} />
      <PageHero
        eyebrow={`${s.group} · Service`}
        title={s.title}
        subtitle={s.summary}
        crumbs={[{ label: s.group, to: `/${s.group.toLowerCase()}` }, { label: s.title }]}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-warm/15 bg-warm/5 px-4 py-2 text-sm text-warm/70"><Clock className="h-4 w-4 text-gold" /> {s.duration}</span>
          <span className="inline-flex items-center gap-2 rounded-full border border-warm/15 bg-warm/5 px-4 py-2 text-sm text-warm/70"><Tag className="h-4 w-4 text-gold" /> {s.cost}</span>
        </div>
      </PageHero>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-14">
          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold"><Icon className="h-7 w-7" strokeWidth={1.4} /></span>
              <p className="font-playfair text-2xl text-navy">{s.tagline}</p>
            </div>
          </Reveal>
          <Block title="What is it?" text={s.what} />
          <Block title="Why it matters" text={s.why} />
          <Block title="Who it's for" text={s.who} />

          <Reveal>
            <h2 className="font-playfair text-3xl text-navy mb-6">How Effizent Europe helps</h2>
            <div className="space-y-3">
              {s.how.map((h, i) => (
                <div key={i} className="flex items-start gap-4 rounded-2xl bg-white border border-navy/10 p-5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-olive/15 text-olive"><Check className="h-4 w-4" /></span>
                  <p className="text-navy/75">{h}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-playfair text-3xl text-navy mb-6 flex items-center gap-3"><HelpCircle className="h-7 w-7 text-gold" /> Frequently asked</h2>
            <Accordion type="single" collapsible className="w-full">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-500 text-navy hover:text-gold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-navy/65 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <p className="mt-4 text-sm text-navy/60">
              <a href="/faq.html" data-testid="service-faq-full-link" className="text-maroon-accent font-600 link-underline">See our full FAQ</a>
            </p>
          </Reveal>
        </div>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-28 h-fit space-y-6">
          <div className="rounded-3xl bg-navy text-warm p-7">
            <p className="accent-eyebrow text-gold">Get started</p>
            <p className="font-playfair text-2xl mt-3">Book a free consultation</p>
            <p className="text-warm/60 text-sm mt-3">Speak with a Portugal specialist about {s.title.toLowerCase()}.</p>
            <button data-testid="service-cta" onClick={() => openConsultation(s.title)} className="mt-6 w-full rounded-full bg-gold py-3 font-600 text-navy hover:bg-gold-soft transition-colors">Begin My Journey</button>
          </div>
          {s.related?.length > 0 && (
            <div className="rounded-3xl border border-navy/10 bg-white p-7">
              <p className="accent-eyebrow text-terracotta">Related services</p>
              <div className="mt-4 space-y-2">
                {s.related.map((r) => {
                  const rs = get(r);
                  if (!rs) return null;
                  return (
                    <Link key={r} to={`/services/${r}`} data-testid={`related-${r}`} className="group flex items-center justify-between rounded-xl px-4 py-3 hover:bg-warm transition-colors">
                      <span className="text-navy/80 text-sm group-hover:text-navy">{rs.title}</span>
                      <ArrowRight className="h-4 w-4 text-navy/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      </div>
    </Layout>
  );
}

function Block({ title, text }) {
  return (
    <Reveal>
      <h2 className="font-playfair text-3xl text-navy mb-3">{title}</h2>
      <p className="text-navy/65 text-lg leading-relaxed">{text}</p>
    </Reveal>
  );
}
